import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const API_BASE = "https://dairy.server.statify.co.ke/api";

// Colors
const primaryColor = "#024a59";
const accentColor = "#ffa916";
const errorColor = "#b00020";

export default function PayrollProcessingPage() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [payrollItems, setPayrollItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fetchLoading, setFetchLoading] = useState({
    employees: false,
    payrollItems: false,
    payrolls: false
  });
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    employee_id: "",
    employee_no: "",
    employee_name: "",
    payroll_period: "",
    earnings: [{ code: "", name: "", amount: "" }],
    deductions: [{ code: "", name: "", amount: "" }],
  });

  // Enhanced data fetching with proper error handling and data structure
  const fetchPayrolls = async () => {
    try {
      setFetchLoading(prev => ({ ...prev, payrolls: true }));
      setError("");
      console.log("🔍 Fetching payrolls from:", `${API_BASE}/payroll_processing/`);
      
      const res = await axios.get(`${API_BASE}/payroll_processing/`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log("📦 Payrolls API Response:", res);
      console.log("📊 Payrolls data:", res.data);

      let data = [];
      
      // Handle multiple response structures
      if (Array.isArray(res.data)) {
        data = res.data;
        console.log("✅ Using direct array for payrolls");
      } else if (res.data && Array.isArray(res.data.records)) {
        data = res.data.records;
        console.log("✅ Using res.data.records for payrolls");
      } else if (res.data && Array.isArray(res.data.data)) {
        data = res.data.data;
        console.log("✅ Using res.data.data for payrolls");
      } else if (res.data && Array.isArray(res.data.payrolls)) {
        data = res.data.payrolls;
        console.log("✅ Using res.data.payrolls for payrolls");
      } else if (res.data && Array.isArray(res.data.results)) {
        data = res.data.results;
        console.log("✅ Using res.data.results for payrolls");
      } else if (res.data && Array.isArray(res.data.items)) {
        data = res.data.items;
        console.log("✅ Using res.data.items for payrolls");
      } else if (res.data && typeof res.data === 'object') {
        // Look for any array property
        const arrayProperties = Object.keys(res.data).filter(key => 
          Array.isArray(res.data[key])
        );
        
        if (arrayProperties.length > 0) {
          data = res.data[arrayProperties[0]];
          console.log(`✅ Using array property '${arrayProperties[0]}' for payrolls`);
        } else {
          data = [res.data];
          console.log("✅ Wrapping single object in array for payrolls");
        }
      } else {
        console.warn("❌ Unexpected payrolls structure:", res.data);
        data = [];
      }

      console.log("🎯 Processed payrolls data:", data);
      console.log("📝 Number of payroll records:", data.length);
      
      // Enhanced: Merge employee data with payroll records for display
      const mergedData = data.map(payroll => {
        const employee = employees.find(emp => 
          emp.id === payroll.employee_id || 
          emp.employee_id === payroll.employee_id ||
          emp.id === payroll.employee?.id
        );
        
        console.log(`🔗 Merging payroll ${payroll.id} with employee:`, employee);
        
        // Create display data but preserve original structure
        const displayData = {
          ...payroll,
          // Ensure we have display fields for the table
          employee_no: payroll.employee_no || 
                      employee?.employee_id || 
                      employee?.employee_number || 
                      payroll.employee?.employee_id ||
                      "—",
          employee_name: payroll.employee_name || 
                        employee?.name || 
                        (employee ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim() : "") || 
                        payroll.employee?.name ||
                        "—",
        };
        
        return displayData;
      });

      console.log("🔄 Merged payroll data with employee info:", mergedData);
      
      setRows(mergedData);
      
    } catch (err) {
      console.error("❌ Error fetching payrolls:", err);
      let errorMessage = "Failed to fetch payroll records";
      
      if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        errorMessage = "Network error: Cannot connect to server. Please check if the server is running.";
      } else if (err.response) {
        errorMessage = err.response.data?.message || 
                      err.response.data?.error || 
                      `Server error: ${err.response.status} - ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = "No response from server. Please check if the backend is running.";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setFetchLoading(prev => ({ ...prev, payrolls: false }));
    }
  };

  // Updated employee fetch to match your API structure
  const fetchEmployees = async () => {
    try {
      setFetchLoading(prev => ({ ...prev, employees: true }));
      setError("");
      console.log("🔍 Fetching employees from:", `${API_BASE}/employees/`);
      
      const res = await axios.get(`${API_BASE}/employees/?per_page=1000`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log("📦 Employees API Response:", res);
      console.log("📊 Employees data:", res.data);

      let data = [];
      
      // Handle the employee API response structure
      if (res.data && Array.isArray(res.data.employees)) {
        data = res.data.employees;
        console.log("✅ Using res.data.employees array");
      } else if (Array.isArray(res.data)) {
        data = res.data;
        console.log("✅ Using direct array for employees");
      } else if (res.data && Array.isArray(res.data.data)) {
        data = res.data.data;
        console.log("✅ Using res.data.data for employees");
      } else if (res.data && typeof res.data === 'object') {
        const arrayProperties = Object.keys(res.data).filter(key => 
          Array.isArray(res.data[key])
        );
        
        if (arrayProperties.length > 0) {
          data = res.data[arrayProperties[0]];
          console.log(`✅ Using array property '${arrayProperties[0]}' for employees`);
        } else {
          data = [res.data];
          console.log("✅ Wrapping single object in array for employees");
        }
      } else {
        console.warn("❌ Unexpected employees structure:", res.data);
        data = [];
      }

      console.log("🎯 Processed employees data:", data);
      console.log("📝 Number of employee records:", data.length);
      
      if (data.length > 0) {
        console.log("👤 Sample employee record:", data[0]);
        console.log("🔑 Available keys in employee:", Object.keys(data[0]));
      }
      
      setEmployees(data);
      
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      let errorMessage = "Failed to fetch employees";
      
      if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        errorMessage = "Network error: Cannot connect to server.";
      } else if (err.response) {
        errorMessage = err.response.data?.message || 
                      err.response.data?.error || 
                      `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response from server.";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setFetchLoading(prev => ({ ...prev, employees: false }));
    }
  };

  const fetchPayrollItems = async () => {
    try {
      setFetchLoading(prev => ({ ...prev, payrollItems: true }));
      setError("");
      console.log("🔍 Fetching payroll items from:", `${API_BASE}/payroll/`);
      
      const res = await axios.get(`${API_BASE}/payroll/`, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log("📦 Payroll Items API Response:", res);
      console.log("📊 Payroll Items data:", res.data);

      let data = [];
      
      if (Array.isArray(res.data)) {
        data = res.data;
        console.log("✅ Using direct array for payroll items");
      } else if (res.data && Array.isArray(res.data.data)) {
        data = res.data.data;
        console.log("✅ Using res.data.data for payroll items");
      } else if (res.data && Array.isArray(res.data.items)) {
        data = res.data.items;
        console.log("✅ Using res.data.items for payroll items");
      } else if (res.data && Array.isArray(res.data.payrolls)) {
        data = res.data.payrolls;
        console.log("✅ Using res.data.payrolls for payroll items");
      } else if (res.data && typeof res.data === 'object') {
        const arrayProperties = Object.keys(res.data).filter(key => 
          Array.isArray(res.data[key])
        );
        
        if (arrayProperties.length > 0) {
          data = res.data[arrayProperties[0]];
          console.log(`✅ Using array property '${arrayProperties[0]}' for payroll items`);
        } else {
          data = [res.data];
          console.log("✅ Wrapping single object in array for payroll items");
        }
      } else {
        console.warn("❌ Unexpected payroll items structure:", res.data);
        data = [];
      }

      console.log("🎯 Processed payroll items data:", data);
      console.log("📝 Number of payroll item records:", data.length);
      
      if (data.length > 0) {
        console.log("🔑 Available keys in payroll items:", Object.keys(data[0]));
      }
      
      setPayrollItems(data);
      
    } catch (err) {
      console.error("❌ Error fetching payroll items:", err);
      let errorMessage = "Failed to fetch payroll items";
      
      if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
        errorMessage = "Network error: Cannot connect to server.";
      } else if (err.response) {
        errorMessage = err.response.data?.message || 
                      err.response.data?.error || 
                      `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response from server.";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setFetchLoading(prev => ({ ...prev, payrollItems: false }));
    }
  };

  // Enhanced data fetching with proper sequencing and dependency
  useEffect(() => {
    const loadData = async () => {
      try {
        // First load employees, then payrolls (so we can merge the data)
        await fetchEmployees();
        await fetchPayrolls(); // This will now use the employees data
        await fetchPayrollItems();
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    
    loadData();
  }, []);

  // Re-fetch payrolls when employees change to ensure proper merging
  useEffect(() => {
    if (employees.length > 0 && rows.length === 0) {
      fetchPayrolls();
    }
  }, [employees.length]);

  // Enhanced employee selection with proper field mapping
  const handleEmployeeChange = (employeeId) => {
    console.log("👤 Selected employee ID:", employeeId);
    
    const employee = employees.find(emp => 
      emp.id == employeeId || 
      emp.employee_id == employeeId
    );
    
    console.log("👤 Found employee:", employee);
    
    if (employee) {
      // Map employee fields based on your API structure
      const employeeData = {
        employee_id: employee.id || employee.employee_id,
        employee_no: employee.employee_id || employee.employee_number || employee.id,
        employee_name: employee.name || 
                      `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 
                      employee.employee_name || 
                      "Unknown"
      };
      
      console.log("📋 Mapped employee data:", employeeData);
      
      setFormData(prev => ({
        ...prev,
        ...employeeData
      }));
    } else {
      console.log("❌ Employee not found with ID:", employeeId);
      setFormData(prev => ({
        ...prev,
        employee_id: employeeId,
        employee_no: "",
        employee_name: "",
      }));
    }
  };

  // Handle date change - FIXED VERSION
  const handleDateChange = (date) => {
    console.log("📅 Date selected:", date);
    setSelectedDate(date);
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const payrollPeriod = `${year}-${month}`;
      console.log("📅 Setting payroll period:", payrollPeriod);
      setFormData(prev => ({
        ...prev,
        payroll_period: payrollPeriod
      }));
    } else {
      console.log("📅 Date is null, clearing payroll period");
      setFormData(prev => ({
        ...prev,
        payroll_period: ""
      }));
    }
  };

  // Add new earning line
  const addEarningLine = () => {
    setFormData(prev => ({
      ...prev,
      earnings: [...prev.earnings, { code: "", name: "", amount: "" }]
    }));
  };

  // Add new deduction line
  const addDeductionLine = () => {
    setFormData(prev => ({
      ...prev,
      deductions: [...prev.deductions, { code: "", name: "", amount: "" }]
    }));
  };

  // Remove earning line
  const removeEarningLine = (index) => {
    if (formData.earnings.length > 1) {
      setFormData(prev => ({
        ...prev,
        earnings: prev.earnings.filter((_, i) => i !== index)
      }));
    }
  };

  // Remove deduction line
  const removeDeductionLine = (index) => {
    if (formData.deductions.length > 1) {
      setFormData(prev => ({
        ...prev,
        deductions: prev.deductions.filter((_, i) => i !== index)
      }));
    }
  };

  // Handle payroll item selection for earnings
  const handleEarningItemChange = (index, payrollItemCode) => {
    console.log("💰 Selecting earning item:", payrollItemCode);
    const payrollItem = payrollItems.find(item => item.code === payrollItemCode);
    console.log("📋 Found payroll item:", payrollItem);
    
    if (payrollItem) {
      setFormData(prev => {
        const newEarnings = [...prev.earnings];
        newEarnings[index] = {
          code: payrollItem.code,
          name: payrollItem.name,
          amount: newEarnings[index]?.amount || ""
        };
        return { ...prev, earnings: newEarnings };
      });
    }
  };

  // Handle payroll item selection for deductions
  const handleDeductionItemChange = (index, payrollItemCode) => {
    console.log("💸 Selecting deduction item:", payrollItemCode);
    const payrollItem = payrollItems.find(item => item.code === payrollItemCode);
    console.log("📋 Found payroll item:", payrollItem);
    
    if (payrollItem) {
      setFormData(prev => {
        const newDeductions = [...prev.deductions];
        newDeductions[index] = {
          code: payrollItem.code,
          name: payrollItem.name,
          amount: newDeductions[index]?.amount || ""
        };
        return { ...prev, deductions: newDeductions };
      });
    }
  };

  // Handle amount change
  const handleAmountChange = (type, index, value) => {
    // Ensure value is positive number
    const numValue = Math.abs(parseFloat(value) || 0);
    
    setFormData(prev => {
      const newItems = [...prev[type]];
      newItems[index] = { ...newItems[index], amount: numValue.toString() };
      return { ...prev, [type]: newItems };
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const gross = formData.earnings.reduce((total, earning) => {
      return total + (parseFloat(earning.amount) || 0);
    }, 0);

    const totalDeductions = formData.deductions.reduce((total, deduction) => {
      return total + (parseFloat(deduction.amount) || 0);
    }, 0);

    const net = gross - totalDeductions;

    return { gross, totalDeductions, net };
  };

  const totals = calculateTotals();

  // CRUD Operations - FIXED VERSION
  const handleAdd = () => {
    console.log("➕ Opening add payroll modal");
    setEditingRecord(null);
    
    // Set initial form data with current date as payroll period
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const payrollPeriod = `${year}-${month}`;
    
    console.log("📅 Initial payroll period:", payrollPeriod);
    
    setSelectedDate(currentDate);
    setFormData({
      employee_id: "",
      employee_no: "",
      employee_name: "",
      payroll_period: payrollPeriod, // Set initial payroll period
      earnings: [{ code: "", name: "", amount: "" }],
      deductions: [{ code: "", name: "", amount: "" }],
    });
    setOpen(true);
    setError("");
  };

  const handleEdit = (row) => {
    console.log("✏️ Editing payroll record:", row);
    setEditingRecord(row);
    
    // Convert payroll period to date
    let date = new Date();
    if (row.payroll_period) {
      const [year, month] = row.payroll_period.split('-');
      date = new Date(parseInt(year), parseInt(month) - 1);
    }
    
    console.log("📅 Setting selected date:", date);
    setSelectedDate(date);
    
    // Parse earnings and deductions from the record - FIXED VERSION
    let earnings = [{ code: "", name: "", amount: "" }];
    let deductions = [{ code: "", name: "", amount: "" }];
    
    // Handle different data structures
    if (row.earnings && Array.isArray(row.earnings) && row.earnings.length > 0) {
      earnings = row.earnings.map(earning => ({
        code: earning.code || "",
        name: earning.name || "",
        amount: earning.amount ? earning.amount.toString() : ""
      }));
    } else if (row.earnings && typeof row.earnings === 'object') {
      // Convert object to array
      earnings = Object.keys(row.earnings).map(key => ({
        code: key,
        name: key,
        amount: row.earnings[key] ? row.earnings[key].toString() : ""
      }));
    }
    
    if (row.deductions && Array.isArray(row.deductions) && row.deductions.length > 0) {
      deductions = row.deductions.map(deduction => ({
        code: deduction.code || "",
        name: deduction.name || "",
        amount: deduction.amount ? deduction.amount.toString() : ""
      }));
    } else if (row.deductions && typeof row.deductions === 'object') {
      // Convert object to array
      deductions = Object.keys(row.deductions).map(key => ({
        code: key,
        name: key,
        amount: row.deductions[key] ? row.deductions[key].toString() : ""
      }));
    }
    
    // Enhanced employee data mapping for edit
    const employeeData = {
      employee_id: row.employee_id || row.employee?.id || "",
      employee_no: row.employee_no || 
                  row.employee?.employee_id || 
                  row.employee?.employee_number || 
                  "",
      employee_name: row.employee_name || 
                    row.employee?.name || 
                    (row.employee ? `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.trim() : "") || 
                    ""
    };
    
    console.log("📋 Employee data for edit:", employeeData);
    console.log("📅 Payroll period for edit:", row.payroll_period);
    console.log("💰 Earnings data for edit:", earnings);
    console.log("💸 Deductions data for edit:", deductions);
    
    setFormData({
      ...employeeData,
      payroll_period: row.payroll_period || "",
      earnings: earnings,
      deductions: deductions,
    });
    setOpen(true);
    setError("");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This payroll record will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: primaryColor,
      cancelButtonColor: errorColor,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      try {
        console.log("🗑️ Deleting payroll record ID:", id);
        await axios.delete(`${API_BASE}/payroll_processing/${id}`);
        Swal.fire("Deleted!", "Payroll record deleted successfully.", "success");
        fetchPayrolls();
      } catch (err) {
        console.error("❌ Error deleting:", err);
        let errorMessage = "Failed to delete payroll record";
        
        if (err.response) {
          errorMessage = err.response.data?.message || 
                        err.response.data?.error || 
                        `Server error: ${err.response.status}`;
        }
        
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };

  // FIXED SAVE FUNCTION - Proper payload structure
  const handleSave = async () => {
    console.log("💾 Saving payroll record...");
    console.log("📋 Current formData:", formData);
    
    // Enhanced validation with detailed logging
    if (!formData.employee_id) {
      console.log("❌ Validation failed: Missing employee_id");
      Swal.fire("Missing Fields", "Employee is required.", "warning");
      return;
    }

    if (!formData.payroll_period) {
      console.log("❌ Validation failed: Missing payroll_period");
      Swal.fire("Missing Fields", "Payroll Period is required.", "warning");
      return;
    }

    console.log("✅ Validation passed - Employee ID:", formData.employee_id, "Payroll Period:", formData.payroll_period);

    // Validate earnings and deductions
    const hasEmptyEarnings = formData.earnings.some(earning => 
      (earning.code && !earning.amount) || (!earning.code && earning.amount)
    );
    const hasEmptyDeductions = formData.deductions.some(deduction => 
      (deduction.code && !deduction.amount) || (!deduction.code && deduction.amount)
    );

    if (hasEmptyEarnings || hasEmptyDeductions) {
      Swal.fire("Invalid Data", "Please ensure all payroll items have both code and amount filled.", "warning");
      return;
    }

    // Filter out empty lines and prepare payload - FIXED STRUCTURE
    const payload = {
      employee_id: parseInt(formData.employee_id),
      payroll_period: formData.payroll_period,
      earnings: formData.earnings
        .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
        .reduce((acc, item) => {
          acc[item.code] = parseFloat(item.amount);
          return acc;
        }, {}),
      deductions: formData.deductions
        .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
        .reduce((acc, item) => {
          acc[item.code] = parseFloat(item.amount);
          return acc;
        }, {}),
    };

    // Alternative payload structure if objects don't work
    const alternativePayload = {
      employee_id: parseInt(formData.employee_id),
      payroll_period: formData.payroll_period,
      earnings: formData.earnings
        .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
        .map(item => ({
          code: item.code,
          name: item.name,
          amount: parseFloat(item.amount)
        })),
      deductions: formData.deductions
        .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
        .map(item => ({
          code: item.code,
          name: item.name,
          amount: parseFloat(item.amount)
        })),
    };

    console.log("📤 Saving payload (object format):", payload);
    console.log("📤 Saving payload (array format):", alternativePayload);

    try {
      setLoading(true);
      setError("");
      
      let response;
      if (editingRecord) {
        console.log("🔄 Updating existing record:", editingRecord.id);
        // Try object format first, fall back to array format
        try {
          response = await axios.put(`${API_BASE}/payroll_processing/${editingRecord.id}`, payload, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          });
        } catch (formatError) {
          console.log("🔄 Trying alternative payload format...");
          response = await axios.put(`${API_BASE}/payroll_processing/${editingRecord.id}`, alternativePayload, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          });
        }
        Swal.fire("Updated!", "Payroll record updated successfully.", "success");
      } else {
        console.log("🆕 Creating new record");
        // Try object format first, fall back to array format
        try {
          response = await axios.post(`${API_BASE}/payroll_processing/`, payload, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          });
        } catch (formatError) {
          console.log("🔄 Trying alternative payload format...");
          response = await axios.post(`${API_BASE}/payroll_processing/`, alternativePayload, {
            headers: { 'Content-Type': 'application/json' },
            timeout: 10000
          });
        }
        Swal.fire("Saved!", "Payroll record added successfully.", "success");
      }
      
      console.log("✅ Save response:", response);
      setOpen(false);
      
      // Refresh data with a small delay to ensure backend has processed
      setTimeout(() => {
        fetchPayrolls();
      }, 500);
      
    } catch (err) {
      console.error("❌ Error saving:", err);
      let errorMessage = "Failed to save payroll record";
      
      if (err.response) {
        errorMessage = err.response.data?.message || 
                      err.response.data?.error || 
                      `Server error: ${err.response.status}`;
        console.error("🚨 Save error details:", err.response.data);
        
        // Provide more specific error messages
        if (err.response.status === 400) {
          errorMessage = "Bad request: Check that all required fields are filled correctly.";
        } else if (err.response.status === 404) {
          errorMessage = "Resource not found. The employee or payroll item may not exist.";
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please check the backend logs.";
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }
      
      setError(errorMessage);
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Enhanced row totals calculation with multiple data structure support
  const calculateRowTotals = (row) => {
    let gross = 0;
    let deductions = 0;

    // Try different possible data structures
    if (row.gross_pay !== undefined && row.gross_pay !== null) {
      gross = parseFloat(row.gross_pay) || 0;
    } else if (row.total_earnings !== undefined && row.total_earnings !== null) {
      gross = parseFloat(row.total_earnings) || 0;
    } else if (row.earnings) {
      if (Array.isArray(row.earnings)) {
        gross = row.earnings.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      } else if (typeof row.earnings === 'object') {
        gross = Object.values(row.earnings).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
      }
    }

    if (row.total_deductions !== undefined && row.total_deductions !== null) {
      deductions = parseFloat(row.total_deductions) || 0;
    } else if (row.deductions) {
      if (Array.isArray(row.deductions)) {
        deductions = row.deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      } else if (typeof row.deductions === 'object') {
        deductions = Object.values(row.deductions).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0);
      }
    }
    
    const net = gross - deductions;
    
    return { gross, deductions, net };
  };

  // Enhanced employee display data mapping
  const getEmployeeDisplayData = (row) => {
    // First check if we have direct employee_no and employee_name (from merged data)
    if (row.employee_no && row.employee_name && row.employee_no !== "—" && row.employee_name !== "—") {
      return {
        employeeNo: row.employee_no,
        employeeName: row.employee_name
      };
    }
    
    // Fallback to employee data object
    const employeeNo = row.employee_no || 
                      row.employee?.employee_id || 
                      row.employee?.employee_number || 
                      row.employee_data?.employee_id ||
                      row.employee_data?.employee_number ||
                      "—";
                      
    const employeeName = row.employee_name || 
                        row.employee?.name || 
                        (row.employee ? `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.trim() : "") || 
                        row.employee_data?.name ||
                        (row.employee_data ? `${row.employee_data.first_name || ''} ${row.employee_data.last_name || ''}`.trim() : "") ||
                        "—";
    
    return { employeeNo, employeeName };
  };

  // Refresh all data with proper sequencing
  const handleRefreshAll = async () => {
    try {
      setError("");
      await fetchEmployees();
      await fetchPayrolls(); // This will use the updated employees
      await fetchPayrollItems();
      Swal.fire("Refreshed!", "All data has been refreshed.", "success");
    } catch (error) {
      console.error("Error refreshing data:", error);
      Swal.fire("Error", "Failed to refresh data.", "error");
    }
  };

  // Table columns
  const columns = [
    { label: "Employee No", key: "employee_no" },
    { label: "Employee Name", key: "employee_name" },
    { label: "Payroll Period", key: "payroll_period" },
    { label: "Gross Pay", key: "gross_pay" },
    { label: "Deductions", key: "total_deductions" },
    { label: "Net Pay", key: "net_pay" },
    { label: "Actions", key: "actions" },
  ];

  // Filter payroll items by type
  const earningItems = payrollItems.filter(item => 
    item.type?.toLowerCase() === "earning" || 
    item.category?.toLowerCase() === "earning" ||
    (item.type === undefined && item.category === undefined) // Include all if no type specified
  );
  
  const deductionItems = payrollItems.filter(item => 
    item.type?.toLowerCase() === "deduction" || 
    item.category?.toLowerCase() === "deduction"
  );

  console.log("💰 Earning items:", earningItems);
  console.log("💸 Deduction items:", deductionItems);
  console.log("👥 Employees data:", employees);
  console.log("📋 Current formData state:", formData);
  console.log("📊 Current rows data:", rows);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ color: primaryColor, fontWeight: 700 }}>
            Payroll Processing
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ 
              backgroundColor: accentColor, 
              color: "#fff",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "#e59410" },
              fontWeight: 600,
              px: 3,
            }}
            onClick={handleAdd}
            disabled={fetchLoading.employees || fetchLoading.payrollItems}
          >
            Add Payroll
          </Button>
        </Stack>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading states */}
        {(fetchLoading.employees || fetchLoading.payrollItems) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <CircularProgress sx={{ color: primaryColor }} />
            <Typography sx={{ ml: 2 }}>
              Loading payroll data...
            </Typography>
          </Box>
        )}

        {/* Data Refresh Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            onClick={handleRefreshAll}
            variant="outlined"
            sx={{ 
              borderColor: primaryColor,
              color: primaryColor,
              "&:hover": { borderColor: primaryColor, backgroundColor: "rgba(2, 74, 89, 0.04)" }
            }}
            disabled={fetchLoading.payrolls || fetchLoading.employees || fetchLoading.payrollItems}
          >
            Refresh All Data
          </Button>
        </Box>

        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 3,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: primaryColor }}>
              <TableRow>
                {columns.map((col) => (
                  <TableCell 
                    key={col.key} 
                    sx={{ 
                      color: "#fff", 
                      fontWeight: "bold",
                      fontSize: "1rem",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fetchLoading.payrolls ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: primaryColor }} />
                    <Typography sx={{ mt: 1 }}>Loading payroll records...</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                      No payroll records found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 400, mb: 2 }}>
                      This could mean:
                      <br/>• No payroll records exist in the database
                      <br/>• There's a connection issue with the server
                      <br/>• The API response structure is different than expected
                    </Typography>
                    <Button
                      startIcon={<Add />}
                      onClick={handleAdd}
                      variant="contained"
                      sx={{
                        bgcolor: accentColor,
                        "&:hover": { bgcolor: "#e59410" },
                      }}
                    >
                      Create First Payroll Record
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const totals = calculateRowTotals(row);
                  const { employeeNo, employeeName } = getEmployeeDisplayData(row);
                  
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {employeeNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employeeName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: primaryColor, fontWeight: 600 }}>
                          {row.payroll_period || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
                        {totals.gross.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ color: errorColor }}>
                        {totals.deductions.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: accentColor }}>
                        {totals.net.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            sx={{ color: primaryColor }}
                            onClick={() => handleEdit(row)}
                            title="Edit"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: errorColor }}
                            onClick={() => handleDelete(row.id)}
                            title="Delete"
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Modal */}
        <Dialog 
          open={open} 
          onClose={() => !loading && setOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle 
            sx={{ 
              backgroundColor: primaryColor, 
              color: "#fff",
              fontWeight: "bold",
              fontSize: "1.2rem",
            }}
          >
            {editingRecord ? "Edit Payroll Record" : "Add Payroll Record"}
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Stack spacing={3}>
              {/* Employee Selection */}
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  fullWidth
                  label="Employee"
                  value={formData.employee_id}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  required
                  error={!formData.employee_id}
                  helperText={!formData.employee_id ? "Employee is required" : ""}
                  disabled={fetchLoading.employees}
                >
                  <MenuItem value="">Select Employee</MenuItem>
                  {employees.map((emp) => {
                    // Map employee display name based on available fields
                    const displayName = emp.name || 
                                      `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 
                                      emp.employee_name || 
                                      `Employee ${emp.id}`;
                                      
                    const employeeId = emp.employee_id || emp.employee_number || emp.id;
                    
                    return (
                      <MenuItem key={emp.id} value={emp.id}>
                        {employeeId} - {displayName}
                      </MenuItem>
                    );
                  })}
                </TextField>
                
                <TextField
                  fullWidth
                  label="Employee Name"
                  value={formData.employee_name}
                  disabled
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled from employee selection"
                />
              </Stack>

              {/* Payroll Period */}
              <DatePicker
                label="Payroll Period"
                views={['year', 'month']}
                value={selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    fullWidth 
                    required
                    error={!formData.payroll_period}
                    helperText={!formData.payroll_period ? "Payroll period is required" : `Selected: ${formData.payroll_period}`}
                  />
                )}
              />

              {/* Earnings Section */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: primaryColor }}>
                    Earnings
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={addEarningLine}
                    sx={{ color: primaryColor }}
                  >
                    Add Earning
                  </Button>
                </Stack>
                
                {formData.earnings.map((earning, index) => (
                  <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <TextField
                      select
                      label="Payroll Item"
                      value={earning.code}
                      onChange={(e) => handleEarningItemChange(index, e.target.value)}
                      sx={{ flex: 2 }}
                      disabled={fetchLoading.payrollItems}
                    >
                      <MenuItem value="">Select Earning Item</MenuItem>
                      {earningItems.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.code} - {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    
                    <TextField
                      label="Amount"
                      type="number"
                      value={earning.amount}
                      onChange={(e) => handleAmountChange("earnings", index, e.target.value)}
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    
                    <IconButton
                      onClick={() => removeEarningLine(index)}
                      sx={{ color: errorColor }}
                      disabled={formData.earnings.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                ))}
              </Box>

              <Divider />

              {/* Deductions Section */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ color: primaryColor }}>
                    Deductions
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={addDeductionLine}
                    sx={{ color: primaryColor }}
                  >
                    Add Deduction
                  </Button>
                </Stack>
                
                {formData.deductions.map((deduction, index) => (
                  <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <TextField
                      select
                      label="Payroll Item"
                      value={deduction.code}
                      onChange={(e) => handleDeductionItemChange(index, e.target.value)}
                      sx={{ flex: 2 }}
                      disabled={fetchLoading.payrollItems}
                    >
                      <MenuItem value="">Select Deduction Item</MenuItem>
                      {deductionItems.map((item) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.code} - {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    
                    <TextField
                      label="Amount"
                      type="number"
                      value={deduction.amount}
                      onChange={(e) => handleAmountChange("deductions", index, e.target.value)}
                      sx={{ flex: 1 }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                    
                    <IconButton
                      onClick={() => removeDeductionLine(index)}
                      sx={{ color: errorColor }}
                      disabled={formData.deductions.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                ))}
              </Box>

              {/* Totals */}
              <Box sx={{ 
                backgroundColor: "#f8f9fa", 
                p: 2, 
                borderRadius: 2,
                border: `1px solid #e0e0e0`
              }}>
                <Typography variant="h6" sx={{ mb: 1, color: primaryColor }}>
                  Payroll Summary
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Gross Pay:</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {totals.gross.toFixed(2)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography>Total Deductions:</Typography>
                    <Typography sx={{ color: errorColor, fontWeight: 600 }}>
                      {totals.totalDeductions.toFixed(2)}
                    </Typography>
                  </Stack>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Net Pay:</Typography>
                    <Typography variant="h6" sx={{ color: accentColor, fontWeight: 700 }}>
                      {totals.net.toFixed(2)}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setOpen(false)}
              disabled={loading}
              sx={{ 
                color: primaryColor,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading || !formData.employee_id || !formData.payroll_period}
              sx={{
                backgroundColor: accentColor,
                "&:hover": { backgroundColor: "#e59410" },
                fontWeight: 600,
                px: 3,
                minWidth: 140,
                "&:disabled": {
                  backgroundColor: "#cccccc",
                  color: "#666666"
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                editingRecord ? "Update Payroll" : "Save Payroll"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}






// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   MenuItem,
//   Typography,
//   Box,
//   IconButton,
//   Stack,
//   Divider,
//   CircularProgress,
//   Alert,
// } from "@mui/material";
// import { Add, Delete, Edit } from "@mui/icons-material";
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// const API_BASE = "http://127.0.0.1:5055/api";

// // Colors
// const primaryColor = "#024a59";
// const accentColor = "#ffa916";
// const errorColor = "#b00020";

// export default function PayrollProcessingPage() {
//   const [rows, setRows] = useState([]);
//   const [employees, setEmployees] = useState([]);
//   const [payrollItems, setPayrollItems] = useState([]);
//   const [open, setOpen] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [editingRecord, setEditingRecord] = useState(null);
//   const [selectedDate, setSelectedDate] = useState(new Date());
//   const [fetchLoading, setFetchLoading] = useState({
//     employees: false,
//     payrollItems: false,
//     payrolls: false
//   });
//   const [error, setError] = useState("");

//   const [formData, setFormData] = useState({
//     employee_id: "",
//     employee_no: "",
//     employee_name: "",
//     payroll_period: "",
//     earnings: [{ code: "", name: "", amount: "" }],
//     deductions: [{ code: "", name: "", amount: "" }],
//   });

//   // Enhanced data fetching with better error handling
//   const fetchPayrolls = async () => {
//     try {
//       setFetchLoading(prev => ({ ...prev, payrolls: true }));
//       setError("");
//       console.log("🔍 Fetching payrolls from:", `${API_BASE}/payroll_processing/`);
      
//       const res = await axios.get(`${API_BASE}/payroll_processing/`, {
//         timeout: 10000,
//         headers: { 'Content-Type': 'application/json' }
//       });

//       console.log("📦 Payrolls API Response:", res);
//       console.log("📊 Payrolls data:", res.data);

//       let data = [];
      
//       // Handle multiple response structures
//       if (Array.isArray(res.data)) {
//         data = res.data;
//         console.log("✅ Using direct array for payrolls");
//       } else if (res.data && Array.isArray(res.data.records)) {
//         data = res.data.records;
//         console.log("✅ Using res.data.records for payrolls");
//       } else if (res.data && Array.isArray(res.data.data)) {
//         data = res.data.data;
//         console.log("✅ Using res.data.data for payrolls");
//       } else if (res.data && Array.isArray(res.data.payrolls)) {
//         data = res.data.payrolls;
//         console.log("✅ Using res.data.payrolls for payrolls");
//       } else if (res.data && Array.isArray(res.data.results)) {
//         data = res.data.results;
//         console.log("✅ Using res.data.results for payrolls");
//       } else if (res.data && Array.isArray(res.data.items)) {
//         data = res.data.items;
//         console.log("✅ Using res.data.items for payrolls");
//       } else if (res.data && typeof res.data === 'object') {
//         // Look for any array property
//         const arrayProperties = Object.keys(res.data).filter(key => 
//           Array.isArray(res.data[key])
//         );
        
//         if (arrayProperties.length > 0) {
//           data = res.data[arrayProperties[0]];
//           console.log(`✅ Using array property '${arrayProperties[0]}' for payrolls`);
//         } else {
//           data = [res.data];
//           console.log("✅ Wrapping single object in array for payrolls");
//         }
//       } else {
//         console.warn("❌ Unexpected payrolls structure:", res.data);
//         data = [];
//       }

//       console.log("🎯 Processed payrolls data:", data);
//       console.log("📝 Number of payroll records:", data.length);
      
//       // Enhanced data processing with fallbacks
//       const processedData = data.map(payroll => {
//         // Calculate totals from earnings/deductions if not provided
//         let gross_pay = payroll.gross_pay;
//         let total_deductions = payroll.total_deductions;
//         let net_pay = payroll.net_pay;

//         // If totals not provided, calculate from earnings/deductions
//         if (!gross_pay && payroll.earnings && Array.isArray(payroll.earnings)) {
//           gross_pay = payroll.earnings.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
//         }
        
//         if (!total_deductions && payroll.deductions && Array.isArray(payroll.deductions)) {
//           total_deductions = payroll.deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
//         }
        
//         if (!net_pay) {
//           net_pay = (parseFloat(gross_pay) || 0) - (parseFloat(total_deductions) || 0);
//         }

//         // Get employee data with multiple fallbacks
//         const employee_no = payroll.employee_no || 
//                            payroll.employee?.employee_id || 
//                            payroll.employee?.employee_number || 
//                            payroll.employee_id || 
//                            "—";

//         const employee_name = payroll.employee_name || 
//                              payroll.employee?.name || 
//                              (payroll.employee ? `${payroll.employee.first_name || ''} ${payroll.employee.last_name || ''}`.trim() : "") || 
//                              "—";

//         return {
//           ...payroll,
//           employee_no,
//           employee_name,
//           gross_pay: parseFloat(gross_pay || 0).toFixed(2),
//           total_deductions: parseFloat(total_deductions || 0).toFixed(2),
//           net_pay: parseFloat(net_pay || 0).toFixed(2),
//           // Ensure arrays exist
//           earnings: Array.isArray(payroll.earnings) ? payroll.earnings : [],
//           deductions: Array.isArray(payroll.deductions) ? payroll.deductions : []
//         };
//       });

//       console.log("🔄 Processed payroll data:", processedData);
//       setRows(processedData);
      
//     } catch (err) {
//       console.error("❌ Error fetching payrolls:", err);
//       let errorMessage = "Failed to fetch payroll records";
      
//       if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
//         errorMessage = "Network error: Cannot connect to server. Please check if the server is running.";
//       } else if (err.response) {
//         errorMessage = err.response.data?.message || 
//                       err.response.data?.error || 
//                       `Server error: ${err.response.status} - ${err.response.statusText}`;
//       } else if (err.request) {
//         errorMessage = "No response from server. Please check if the backend is running.";
//       }
      
//       setError(errorMessage);
//       Swal.fire("Error", errorMessage, "error");
//     } finally {
//       setFetchLoading(prev => ({ ...prev, payrolls: false }));
//     }
//   };

//   // Fetch employees
//   const fetchEmployees = async () => {
//     try {
//       setFetchLoading(prev => ({ ...prev, employees: true }));
//       setError("");
//       console.log("🔍 Fetching employees from:", `${API_BASE}/employees/`);
      
//       const res = await axios.get(`${API_BASE}/employees/?per_page=1000`, {
//         timeout: 10000,
//         headers: { 'Content-Type': 'application/json' }
//       });

//       console.log("📦 Employees API Response:", res);

//       let data = [];
      
//       if (res.data && Array.isArray(res.data.employees)) {
//         data = res.data.employees;
//         console.log("✅ Using res.data.employees array");
//       } else if (Array.isArray(res.data)) {
//         data = res.data;
//         console.log("✅ Using direct array for employees");
//       } else if (res.data && Array.isArray(res.data.data)) {
//         data = res.data.data;
//         console.log("✅ Using res.data.data for employees");
//       } else if (res.data && typeof res.data === 'object') {
//         const arrayProperties = Object.keys(res.data).filter(key => 
//           Array.isArray(res.data[key])
//         );
        
//         if (arrayProperties.length > 0) {
//           data = res.data[arrayProperties[0]];
//           console.log(`✅ Using array property '${arrayProperties[0]}' for employees`);
//         } else {
//           data = [res.data];
//           console.log("✅ Wrapping single object in array for employees");
//         }
//       } else {
//         console.warn("❌ Unexpected employees structure:", res.data);
//         data = [];
//       }

//       console.log("🎯 Processed employees data:", data);
//       setEmployees(data);
      
//     } catch (err) {
//       console.error("❌ Error fetching employees:", err);
//       let errorMessage = "Failed to fetch employees";
      
//       if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
//         errorMessage = "Network error: Cannot connect to server.";
//       } else if (err.response) {
//         errorMessage = err.response.data?.message || 
//                       err.response.data?.error || 
//                       `Server error: ${err.response.status}`;
//       } else if (err.request) {
//         errorMessage = "No response from server.";
//       }
      
//       setError(errorMessage);
//       Swal.fire("Error", errorMessage, "error");
//     } finally {
//       setFetchLoading(prev => ({ ...prev, employees: false }));
//     }
//   };

//   const fetchPayrollItems = async () => {
//     try {
//       setFetchLoading(prev => ({ ...prev, payrollItems: true }));
//       setError("");
//       console.log("🔍 Fetching payroll items from:", `${API_BASE}/payroll/`);
      
//       const res = await axios.get(`${API_BASE}/payroll/`, {
//         timeout: 10000,
//         headers: { 'Content-Type': 'application/json' }
//       });

//       console.log("📦 Payroll Items API Response:", res);

//       let data = [];
      
//       if (Array.isArray(res.data)) {
//         data = res.data;
//         console.log("✅ Using direct array for payroll items");
//       } else if (res.data && Array.isArray(res.data.data)) {
//         data = res.data.data;
//         console.log("✅ Using res.data.data for payroll items");
//       } else if (res.data && Array.isArray(res.data.items)) {
//         data = res.data.items;
//         console.log("✅ Using res.data.items for payroll items");
//       } else if (res.data && Array.isArray(res.data.payrolls)) {
//         data = res.data.payrolls;
//         console.log("✅ Using res.data.payrolls for payroll items");
//       } else if (res.data && typeof res.data === 'object') {
//         const arrayProperties = Object.keys(res.data).filter(key => 
//           Array.isArray(res.data[key])
//         );
        
//         if (arrayProperties.length > 0) {
//           data = res.data[arrayProperties[0]];
//           console.log(`✅ Using array property '${arrayProperties[0]}' for payroll items`);
//         } else {
//           data = [res.data];
//           console.log("✅ Wrapping single object in array for payroll items");
//         }
//       } else {
//         console.warn("❌ Unexpected payroll items structure:", res.data);
//         data = [];
//       }

//       console.log("🎯 Processed payroll items data:", data);
//       setPayrollItems(data);
      
//     } catch (err) {
//       console.error("❌ Error fetching payroll items:", err);
//       let errorMessage = "Failed to fetch payroll items";
      
//       if (err.code === 'NETWORK_ERROR' || err.message === 'Network Error') {
//         errorMessage = "Network error: Cannot connect to server.";
//       } else if (err.response) {
//         errorMessage = err.response.data?.message || 
//                       err.response.data?.error || 
//                       `Server error: ${err.response.status}`;
//       } else if (err.request) {
//         errorMessage = "No response from server.";
//       }
      
//       setError(errorMessage);
//       Swal.fire("Error", errorMessage, "error");
//     } finally {
//       setFetchLoading(prev => ({ ...prev, payrollItems: false }));
//     }
//   };

//   // Enhanced data fetching with proper sequencing
//   useEffect(() => {
//     const loadData = async () => {
//       try {
//         await fetchEmployees();
//         await fetchPayrolls();
//         await fetchPayrollItems();
//       } catch (error) {
//         console.error("Error loading data:", error);
//       }
//     };
    
//     loadData();
//   }, []);

//   // Enhanced employee selection
//   const handleEmployeeChange = (employeeId) => {
//     console.log("👤 Selected employee ID:", employeeId);
    
//     const employee = employees.find(emp => 
//       emp.id == employeeId || 
//       emp.employee_id == employeeId
//     );
    
//     console.log("👤 Found employee:", employee);
    
//     if (employee) {
//       const employeeData = {
//         employee_id: employee.id || employee.employee_id,
//         employee_no: employee.employee_id || employee.employee_number || employee.id,
//         employee_name: employee.name || 
//                       `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 
//                       employee.employee_name || 
//                       "Unknown"
//       };
      
//       console.log("📋 Mapped employee data:", employeeData);
      
//       setFormData(prev => ({
//         ...prev,
//         ...employeeData
//       }));
//     } else {
//       console.log("❌ Employee not found with ID:", employeeId);
//       setFormData(prev => ({
//         ...prev,
//         employee_id: employeeId,
//         employee_no: "",
//         employee_name: "",
//       }));
//     }
//   };

//   // Handle date change - FIXED VERSION
//   const handleDateChange = (date) => {
//     console.log("📅 Date selected:", date);
//     setSelectedDate(date);
//     if (date) {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, '0');
//       const payrollPeriod = `${year}-${month}`;
//       console.log("📅 Setting payroll period:", payrollPeriod);
//       setFormData(prev => ({
//         ...prev,
//         payroll_period: payrollPeriod
//       }));
//     } else {
//       console.log("📅 Date is null, clearing payroll period");
//       setFormData(prev => ({
//         ...prev,
//         payroll_period: ""
//       }));
//     }
//   };

//   // Add new earning line
//   const addEarningLine = () => {
//     setFormData(prev => ({
//       ...prev,
//       earnings: [...prev.earnings, { code: "", name: "", amount: "" }]
//     }));
//   };

//   // Add new deduction line
//   const addDeductionLine = () => {
//     setFormData(prev => ({
//       ...prev,
//       deductions: [...prev.deductions, { code: "", name: "", amount: "" }]
//     }));
//   };

//   // Remove earning line
//   const removeEarningLine = (index) => {
//     if (formData.earnings.length > 1) {
//       setFormData(prev => ({
//         ...prev,
//         earnings: prev.earnings.filter((_, i) => i !== index)
//       }));
//     }
//   };

//   // Remove deduction line
//   const removeDeductionLine = (index) => {
//     if (formData.deductions.length > 1) {
//       setFormData(prev => ({
//         ...prev,
//         deductions: prev.deductions.filter((_, i) => i !== index)
//       }));
//     }
//   };

//   // Handle payroll item selection for earnings
//   const handleEarningItemChange = (index, payrollItemCode) => {
//     console.log("💰 Selecting earning item:", payrollItemCode);
//     const payrollItem = payrollItems.find(item => item.code === payrollItemCode);
//     console.log("📋 Found payroll item:", payrollItem);
    
//     if (payrollItem) {
//       setFormData(prev => {
//         const newEarnings = [...prev.earnings];
//         newEarnings[index] = {
//           code: payrollItem.code,
//           name: payrollItem.name,
//           amount: newEarnings[index]?.amount || ""
//         };
//         return { ...prev, earnings: newEarnings };
//       });
//     }
//   };

//   // Handle payroll item selection for deductions
//   const handleDeductionItemChange = (index, payrollItemCode) => {
//     console.log("💸 Selecting deduction item:", payrollItemCode);
//     const payrollItem = payrollItems.find(item => item.code === payrollItemCode);
//     console.log("📋 Found payroll item:", payrollItem);
    
//     if (payrollItem) {
//       setFormData(prev => {
//         const newDeductions = [...prev.deductions];
//         newDeductions[index] = {
//           code: payrollItem.code,
//           name: payrollItem.name,
//           amount: newDeductions[index]?.amount || ""
//         };
//         return { ...prev, deductions: newDeductions };
//       });
//     }
//   };

//   // Handle amount change
//   const handleAmountChange = (type, index, value) => {
//     // Ensure value is positive number
//     const numValue = Math.abs(parseFloat(value) || 0);
    
//     setFormData(prev => {
//       const newItems = [...prev[type]];
//       newItems[index] = { ...newItems[index], amount: numValue.toString() };
//       return { ...prev, [type]: newItems };
//     });
//   };

//   // Calculate totals
//   const calculateTotals = () => {
//     const gross = formData.earnings.reduce((total, earning) => {
//       return total + (parseFloat(earning.amount) || 0);
//     }, 0);

//     const totalDeductions = formData.deductions.reduce((total, deduction) => {
//       return total + (parseFloat(deduction.amount) || 0);
//     }, 0);

//     const net = gross - totalDeductions;

//     return { gross, totalDeductions, net };
//   };

//   const totals = calculateTotals();

//   // CRUD Operations - FIXED VERSION
//   const handleAdd = () => {
//     console.log("➕ Opening add payroll modal");
//     setEditingRecord(null);
    
//     // Set initial form data with current date as payroll period
//     const currentDate = new Date();
//     const year = currentDate.getFullYear();
//     const month = String(currentDate.getMonth() + 1).padStart(2, '0');
//     const payrollPeriod = `${year}-${month}`;
    
//     console.log("📅 Initial payroll period:", payrollPeriod);
    
//     setSelectedDate(currentDate);
//     setFormData({
//       employee_id: "",
//       employee_no: "",
//       employee_name: "",
//       payroll_period: payrollPeriod,
//       earnings: [{ code: "", name: "", amount: "" }],
//       deductions: [{ code: "", name: "", amount: "" }],
//     });
//     setOpen(true);
//     setError("");
//   };

//   const handleEdit = (row) => {
//     console.log("✏️ Editing payroll record:", row);
//     setEditingRecord(row);
    
//     // Convert payroll period to date
//     let date = new Date();
//     if (row.payroll_period) {
//       const [year, month] = row.payroll_period.split('-');
//       date = new Date(parseInt(year), parseInt(month) - 1);
//     }
    
//     console.log("📅 Setting selected date:", date);
//     setSelectedDate(date);
    
//     // Parse earnings and deductions from the record
//     const earnings = row.earnings && Array.isArray(row.earnings) && row.earnings.length > 0 
//       ? row.earnings 
//       : [{ code: "", name: "", amount: "" }];
      
//     const deductions = row.deductions && Array.isArray(row.deductions) && row.deductions.length > 0 
//       ? row.deductions 
//       : [{ code: "", name: "", amount: "" }];
    
//     // Enhanced employee data mapping for edit
//     const employeeData = {
//       employee_id: row.employee_id || row.employee?.id || "",
//       employee_no: row.employee_no || 
//                   row.employee?.employee_id || 
//                   row.employee?.employee_number || 
//                   "",
//       employee_name: row.employee_name || 
//                     row.employee?.name || 
//                     (row.employee ? `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.trim() : "") || 
//                     ""
//     };
    
//     console.log("📋 Employee data for edit:", employeeData);
//     console.log("📅 Payroll period for edit:", row.payroll_period);
    
//     setFormData({
//       ...employeeData,
//       payroll_period: row.payroll_period || "",
//       earnings: earnings,
//       deductions: deductions,
//     });
//     setOpen(true);
//     setError("");
//   };

//   const handleDelete = async (id) => {
//     const result = await Swal.fire({
//       title: "Are you sure?",
//       text: "This payroll record will be permanently deleted!",
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: primaryColor,
//       cancelButtonColor: errorColor,
//       confirmButtonText: "Yes, delete it!",
//       cancelButtonText: "Cancel"
//     });

//     if (result.isConfirmed) {
//       try {
//         console.log("🗑️ Deleting payroll record ID:", id);
//         await axios.delete(`${API_BASE}/payroll_processing/${id}`);
//         Swal.fire("Deleted!", "Payroll record deleted successfully.", "success");
//         fetchPayrolls();
//       } catch (err) {
//         console.error("❌ Error deleting:", err);
//         let errorMessage = "Failed to delete payroll record";
        
//         if (err.response) {
//           errorMessage = err.response.data?.message || 
//                         err.response.data?.error || 
//                         `Server error: ${err.response.status}`;
//         }
        
//         Swal.fire("Error", errorMessage, "error");
//       }
//     }
//   };

//   // FIXED SAVE FUNCTION - Enhanced payload structure
//   const handleSave = async () => {
//     console.log("💾 Saving payroll record...");
//     console.log("📋 Current formData:", formData);
    
//     // Enhanced validation with detailed logging
//     if (!formData.employee_id) {
//       console.log("❌ Validation failed: Missing employee_id");
//       Swal.fire("Missing Fields", "Employee is required.", "warning");
//       return;
//     }

//     if (!formData.payroll_period) {
//       console.log("❌ Validation failed: Missing payroll_period");
//       Swal.fire("Missing Fields", "Payroll Period is required.", "warning");
//       return;
//     }

//     console.log("✅ Validation passed - Employee ID:", formData.employee_id, "Payroll Period:", formData.payroll_period);

//     // Validate earnings and deductions
//     const hasEmptyEarnings = formData.earnings.some(earning => 
//       (earning.code && !earning.amount) || (!earning.code && earning.amount)
//     );
//     const hasEmptyDeductions = formData.deductions.some(deduction => 
//       (deduction.code && !deduction.amount) || (!deduction.code && deduction.amount)
//     );

//     if (hasEmptyEarnings || hasEmptyDeductions) {
//       Swal.fire("Invalid Data", "Please ensure all payroll items have both code and amount filled.", "warning");
//       return;
//     }

//     // Filter out empty lines and prepare payload - ENHANCED VERSION
//     const earningsPayload = formData.earnings
//       .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
//       .map(item => ({
//         code: item.code,
//         name: item.name,
//         amount: parseFloat(item.amount)
//       }));

//     const deductionsPayload = formData.deductions
//       .filter(item => item.code && item.amount && parseFloat(item.amount) > 0)
//       .map(item => ({
//         code: item.code,
//         name: item.name,
//         amount: parseFloat(item.amount)
//       }));

//     // Calculate totals for the payload
//     const gross_pay = earningsPayload.reduce((sum, item) => sum + item.amount, 0);
//     const total_deductions = deductionsPayload.reduce((sum, item) => sum + item.amount, 0);
//     const net_pay = gross_pay - total_deductions;

//     // Enhanced payload structure with multiple possible API expectations
//     const payload = {
//       employee_id: formData.employee_id,
//       payroll_period: formData.payroll_period,
//       earnings: earningsPayload,
//       deductions: deductionsPayload,
//       // Include totals in case API expects them
//       gross_pay: gross_pay,
//       total_deductions: total_deductions,
//       net_pay: net_pay,
//       // Include employee details for better data consistency
//       employee_no: formData.employee_no,
//       employee_name: formData.employee_name
//     };

//     console.log("📤 Saving payload:", payload);

//     try {
//       setLoading(true);
//       setError("");
      
//       let response;
//       if (editingRecord) {
//         console.log("🔄 Updating existing record:", editingRecord.id);
//         response = await axios.put(`${API_BASE}/payroll_processing/${editingRecord.id}`, payload, {
//           headers: { 'Content-Type': 'application/json' },
//           timeout: 10000
//         });
//         Swal.fire("Updated!", "Payroll record updated successfully.", "success");
//       } else {
//         console.log("🆕 Creating new record");
//         response = await axios.post(`${API_BASE}/payroll_processing/`, payload, {
//           headers: { 'Content-Type': 'application/json' },
//           timeout: 10000
//         });
//         Swal.fire("Saved!", "Payroll record added successfully.", "success");
//       }
      
//       console.log("✅ Save response:", response.data);
//       setOpen(false);
      
//       // Refresh data immediately after save
//       await fetchPayrolls();
      
//     } catch (err) {
//       console.error("❌ Error saving:", err);
//       let errorMessage = "Failed to save payroll record";
      
//       if (err.response) {
//         errorMessage = err.response.data?.message || 
//                       err.response.data?.error || 
//                       `Server error: ${err.response.status}`;
//         console.error("🚨 Save error details:", err.response.data);
        
//         // Provide more specific error messages
//         if (err.response.status === 400) {
//           errorMessage = "Bad request: Please check your data format";
//         } else if (err.response.status === 404) {
//           errorMessage = "Endpoint not found. Please check the API URL.";
//         } else if (err.response.status === 500) {
//           errorMessage = "Server error. Please check the backend logs.";
//         }
//       } else if (err.request) {
//         errorMessage = "No response from server. Please check your connection and ensure the backend is running.";
//       } else {
//         errorMessage = err.message || "Unknown error occurred";
//       }
      
//       setError(errorMessage);
//       Swal.fire("Error", errorMessage, "error");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Enhanced row totals calculation
//   const calculateRowTotals = (row) => {
//     let gross = 0;
//     let deductions = 0;

//     // Try different possible data structures
//     if (row.gross_pay !== undefined && row.gross_pay !== null) {
//       gross = parseFloat(row.gross_pay) || 0;
//     } else if (row.earnings && Array.isArray(row.earnings)) {
//       gross = row.earnings.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
//     }

//     if (row.total_deductions !== undefined && row.total_deductions !== null) {
//       deductions = parseFloat(row.total_deductions) || 0;
//     } else if (row.deductions && Array.isArray(row.deductions)) {
//       deductions = row.deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
//     }
    
//     const net = gross - deductions;
    
//     return { 
//       gross: parseFloat(gross).toFixed(2), 
//       deductions: parseFloat(deductions).toFixed(2), 
//       net: parseFloat(net).toFixed(2) 
//     };
//   };

//   // Enhanced employee display data mapping
//   const getEmployeeDisplayData = (row) => {
//     // Multiple fallbacks for employee data
//     const employeeNo = row.employee_no || 
//                       row.employee?.employee_id || 
//                       row.employee?.employee_number || 
//                       row.employee_id || 
//                       "—";
                      
//     const employeeName = row.employee_name || 
//                         row.employee?.name || 
//                         (row.employee ? `${row.employee.first_name || ''} ${row.employee.last_name || ''}`.trim() : "") || 
//                         "—";
    
//     return { employeeNo, employeeName };
//   };

//   // Refresh all data with proper sequencing
//   const handleRefreshAll = async () => {
//     try {
//       setError("");
//       await fetchEmployees();
//       await fetchPayrolls();
//       await fetchPayrollItems();
//       Swal.fire("Refreshed!", "All data has been refreshed.", "success");
//     } catch (error) {
//       console.error("Error refreshing data:", error);
//       Swal.fire("Error", "Failed to refresh data.", "error");
//     }
//   };

//   // Table columns
//   const columns = [
//     { label: "Employee No", key: "employee_no" },
//     { label: "Employee Name", key: "employee_name" },
//     { label: "Payroll Period", key: "payroll_period" },
//     { label: "Gross Pay", key: "gross_pay" },
//     { label: "Deductions", key: "total_deductions" },
//     { label: "Net Pay", key: "net_pay" },
//     { label: "Actions", key: "actions" },
//   ];

//   // Filter payroll items by type
//   const earningItems = payrollItems.filter(item => 
//     item.type?.toLowerCase() === "earning" || 
//     item.category?.toLowerCase() === "earning" ||
//     (item.type === undefined && item.category === undefined)
//   );
  
//   const deductionItems = payrollItems.filter(item => 
//     item.type?.toLowerCase() === "deduction" || 
//     item.category?.toLowerCase() === "deduction"
//   );

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <Box sx={{ p: 3 }}>
//         <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
//           <Typography variant="h4" sx={{ color: primaryColor, fontWeight: 700 }}>
//             Payroll Processing
//           </Typography>
//           <Button
//             variant="contained"
//             startIcon={<Add />}
//             sx={{ 
//               backgroundColor: accentColor, 
//               color: "#fff",
//               borderRadius: "10px",
//               "&:hover": { backgroundColor: "#e59410" },
//               fontWeight: 600,
//               px: 3,
//             }}
//             onClick={handleAdd}
//             disabled={fetchLoading.employees || fetchLoading.payrollItems}
//           >
//             Add Payroll
//           </Button>
//         </Stack>

//         {/* Error Display */}
//         {error && (
//           <Alert severity="error" sx={{ mb: 2 }}>
//             {error}
//           </Alert>
//         )}

//         {/* Loading states */}
//         {(fetchLoading.employees || fetchLoading.payrollItems) && (
//           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
//             <CircularProgress sx={{ color: primaryColor }} />
//             <Typography sx={{ ml: 2 }}>
//               Loading payroll data...
//             </Typography>
//           </Box>
//         )}

//         {/* Data Refresh Button */}
//         <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
//           <Button
//             onClick={handleRefreshAll}
//             variant="outlined"
//             sx={{ 
//               borderColor: primaryColor,
//               color: primaryColor,
//               "&:hover": { borderColor: primaryColor, backgroundColor: "rgba(2, 74, 89, 0.04)" }
//             }}
//             disabled={fetchLoading.payrolls || fetchLoading.employees || fetchLoading.payrollItems}
//           >
//             Refresh All Data
//           </Button>
//         </Box>

//         <TableContainer 
//           component={Paper} 
//           sx={{ 
//             borderRadius: 3,
//             boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
//           }}
//         >
//           <Table>
//             <TableHead sx={{ backgroundColor: primaryColor }}>
//               <TableRow>
//                 {columns.map((col) => (
//                   <TableCell 
//                     key={col.key} 
//                     sx={{ 
//                       color: "#fff", 
//                       fontWeight: "bold",
//                       fontSize: "1rem",
//                     }}
//                   >
//                     {col.label}
//                   </TableCell>
//                 ))}
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {fetchLoading.payrolls ? (
//                 <TableRow>
//                   <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
//                     <CircularProgress sx={{ color: primaryColor }} />
//                     <Typography sx={{ mt: 1 }}>Loading payroll records...</Typography>
//                   </TableCell>
//                 </TableRow>
//               ) : rows.length === 0 ? (
//                 <TableRow>
//                   <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
//                     <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
//                       No payroll records found
//                     </Typography>
//                     <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 400, mb: 2 }}>
//                       This could mean:
//                       <br/>• No payroll records exist in the database
//                       <br/>• There's a connection issue with the server
//                       <br/>• The API response structure is different than expected
//                     </Typography>
//                     <Button
//                       startIcon={<Add />}
//                       onClick={handleAdd}
//                       variant="contained"
//                       sx={{
//                         bgcolor: accentColor,
//                         "&:hover": { bgcolor: "#e59410" },
//                       }}
//                     >
//                       Create First Payroll Record
//                     </Button>
//                   </TableCell>
//                 </TableRow>
//               ) : (
//                 rows.map((row) => {
//                   const totals = calculateRowTotals(row);
//                   const { employeeNo, employeeName } = getEmployeeDisplayData(row);
                  
//                   return (
//                     <TableRow key={row.id} hover>
//                       <TableCell>
//                         <Typography variant="body2" sx={{ fontWeight: 600 }}>
//                           {employeeNo}
//                         </Typography>
//                       </TableCell>
//                       <TableCell>
//                         <Typography variant="body2">
//                           {employeeName}
//                         </Typography>
//                       </TableCell>
//                       <TableCell>
//                         <Typography variant="body2" sx={{ color: primaryColor, fontWeight: 600 }}>
//                           {row.payroll_period || "—"}
//                         </Typography>
//                       </TableCell>
//                       <TableCell sx={{ fontWeight: 600, color: primaryColor }}>
//                         {totals.gross}
//                       </TableCell>
//                       <TableCell sx={{ color: errorColor }}>
//                         {totals.deductions}
//                       </TableCell>
//                       <TableCell sx={{ fontWeight: 700, color: accentColor }}>
//                         {totals.net}
//                       </TableCell>
//                       <TableCell>
//                         <Stack direction="row" spacing={1}>
//                           <IconButton
//                             size="small"
//                             sx={{ color: primaryColor }}
//                             onClick={() => handleEdit(row)}
//                             title="Edit"
//                           >
//                             <Edit />
//                           </IconButton>
//                           <IconButton
//                             size="small"
//                             sx={{ color: errorColor }}
//                             onClick={() => handleDelete(row.id)}
//                             title="Delete"
//                           >
//                             <Delete />
//                           </IconButton>
//                         </Stack>
//                       </TableCell>
//                     </TableRow>
//                   );
//                 })
//               )}
//             </TableBody>
//           </Table>
//         </TableContainer>

//         {/* Add/Edit Modal */}
//         <Dialog 
//           open={open} 
//           onClose={() => !loading && setOpen(false)} 
//           maxWidth="md" 
//           fullWidth
//           PaperProps={{
//             sx: { borderRadius: 3 }
//           }}
//         >
//           <DialogTitle 
//             sx={{ 
//               backgroundColor: primaryColor, 
//               color: "#fff",
//               fontWeight: "bold",
//               fontSize: "1.2rem",
//             }}
//           >
//             {editingRecord ? "Edit Payroll Record" : "Add Payroll Record"}
//           </DialogTitle>
          
//           <DialogContent sx={{ p: 3 }}>
//             {error && (
//               <Alert severity="error" sx={{ mb: 2 }}>
//                 {error}
//               </Alert>
//             )}
            
//             <Stack spacing={3}>
//               {/* Employee Selection */}
//               <Stack direction="row" spacing={2}>
//                 <TextField
//                   select
//                   fullWidth
//                   label="Employee"
//                   value={formData.employee_id}
//                   onChange={(e) => handleEmployeeChange(e.target.value)}
//                   required
//                   error={!formData.employee_id}
//                   helperText={!formData.employee_id ? "Employee is required" : ""}
//                   disabled={fetchLoading.employees}
//                 >
//                   <MenuItem value="">Select Employee</MenuItem>
//                   {employees.map((emp) => {
//                     const displayName = emp.name || 
//                                       `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 
//                                       emp.employee_name || 
//                                       `Employee ${emp.id}`;
                                      
//                     const employeeId = emp.employee_id || emp.employee_number || emp.id;
                    
//                     return (
//                       <MenuItem key={emp.id} value={emp.id}>
//                         {employeeId} - {displayName}
//                       </MenuItem>
//                     );
//                   })}
//                 </TextField>
                
//                 <TextField
//                   fullWidth
//                   label="Employee Name"
//                   value={formData.employee_name}
//                   disabled
//                   InputProps={{ readOnly: true }}
//                   helperText="Auto-filled from employee selection"
//                 />
//               </Stack>

//               {/* Payroll Period */}
//               <DatePicker
//                 label="Payroll Period"
//                 views={['year', 'month']}
//                 value={selectedDate}
//                 onChange={handleDateChange}
//                 renderInput={(params) => (
//                   <TextField 
//                     {...params} 
//                     fullWidth 
//                     required
//                     error={!formData.payroll_period}
//                     helperText={!formData.payroll_period ? "Payroll period is required" : `Selected: ${formData.payroll_period}`}
//                   />
//                 )}
//               />

//               {/* Earnings Section */}
//               <Box>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
//                   <Typography variant="h6" sx={{ color: primaryColor }}>
//                     Earnings
//                   </Typography>
//                   <Button
//                     startIcon={<Add />}
//                     onClick={addEarningLine}
//                     sx={{ color: primaryColor }}
//                   >
//                     Add Earning
//                   </Button>
//                 </Stack>
                
//                 {formData.earnings.map((earning, index) => (
//                   <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
//                     <TextField
//                       select
//                       label="Payroll Item"
//                       value={earning.code}
//                       onChange={(e) => handleEarningItemChange(index, e.target.value)}
//                       sx={{ flex: 2 }}
//                       disabled={fetchLoading.payrollItems}
//                     >
//                       <MenuItem value="">Select Earning Item</MenuItem>
//                       {earningItems.map((item) => (
//                         <MenuItem key={item.code} value={item.code}>
//                           {item.code} - {item.name}
//                         </MenuItem>
//                       ))}
//                     </TextField>
                    
//                     <TextField
//                       label="Amount"
//                       type="number"
//                       value={earning.amount}
//                       onChange={(e) => handleAmountChange("earnings", index, e.target.value)}
//                       sx={{ flex: 1 }}
//                       inputProps={{ min: 0, step: 0.01 }}
//                     />
                    
//                     <IconButton
//                       onClick={() => removeEarningLine(index)}
//                       sx={{ color: errorColor }}
//                       disabled={formData.earnings.length === 1}
//                     >
//                       <Delete />
//                     </IconButton>
//                   </Stack>
//                 ))}
//               </Box>

//               <Divider />

//               {/* Deductions Section */}
//               <Box>
//                 <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
//                   <Typography variant="h6" sx={{ color: primaryColor }}>
//                     Deductions
//                   </Typography>
//                   <Button
//                     startIcon={<Add />}
//                     onClick={addDeductionLine}
//                     sx={{ color: primaryColor }}
//                   >
//                     Add Deduction
//                   </Button>
//                 </Stack>
                
//                 {formData.deductions.map((deduction, index) => (
//                   <Stack key={index} direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
//                     <TextField
//                       select
//                       label="Payroll Item"
//                       value={deduction.code}
//                       onChange={(e) => handleDeductionItemChange(index, e.target.value)}
//                       sx={{ flex: 2 }}
//                       disabled={fetchLoading.payrollItems}
//                     >
//                       <MenuItem value="">Select Deduction Item</MenuItem>
//                       {deductionItems.map((item) => (
//                         <MenuItem key={item.code} value={item.code}>
//                           {item.code} - {item.name}
//                         </MenuItem>
//                       ))}
//                     </TextField>
                    
//                     <TextField
//                       label="Amount"
//                       type="number"
//                       value={deduction.amount}
//                       onChange={(e) => handleAmountChange("deductions", index, e.target.value)}
//                       sx={{ flex: 1 }}
//                       inputProps={{ min: 0, step: 0.01 }}
//                     />
                    
//                     <IconButton
//                       onClick={() => removeDeductionLine(index)}
//                       sx={{ color: errorColor }}
//                       disabled={formData.deductions.length === 1}
//                     >
//                       <Delete />
//                     </IconButton>
//                   </Stack>
//                 ))}
//               </Box>

//               {/* Totals */}
//               <Box sx={{ 
//                 backgroundColor: "#f8f9fa", 
//                 p: 2, 
//                 borderRadius: 2,
//                 border: `1px solid #e0e0e0`
//               }}>
//                 <Typography variant="h6" sx={{ mb: 1, color: primaryColor }}>
//                   Payroll Summary
//                 </Typography>
//                 <Stack spacing={1}>
//                   <Stack direction="row" justifyContent="space-between">
//                     <Typography>Gross Pay:</Typography>
//                     <Typography sx={{ fontWeight: 600 }}>
//                       {totals.gross.toFixed(2)}
//                     </Typography>
//                   </Stack>
//                   <Stack direction="row" justifyContent="space-between">
//                     <Typography>Total Deductions:</Typography>
//                     <Typography sx={{ color: errorColor, fontWeight: 600 }}>
//                       {totals.totalDeductions.toFixed(2)}
//                     </Typography>
//                   </Stack>
//                   <Divider />
//                   <Stack direction="row" justifyContent="space-between">
//                     <Typography variant="h6">Net Pay:</Typography>
//                     <Typography variant="h6" sx={{ color: accentColor, fontWeight: 700 }}>
//                       {totals.net.toFixed(2)}
//                     </Typography>
//                   </Stack>
//                 </Stack>
//               </Box>
//             </Stack>
//           </DialogContent>

//           <DialogActions sx={{ p: 2 }}>
//             <Button 
//               onClick={() => setOpen(false)}
//               disabled={loading}
//               sx={{ 
//                 color: primaryColor,
//                 fontWeight: 600,
//               }}
//             >
//               Cancel
//             </Button>
//             <Button
//               onClick={handleSave}
//               variant="contained"
//               disabled={loading || !formData.employee_id || !formData.payroll_period}
//               sx={{
//                 backgroundColor: accentColor,
//                 "&:hover": { backgroundColor: "#e59410" },
//                 fontWeight: 600,
//                 px: 3,
//                 minWidth: 140,
//                 "&:disabled": {
//                   backgroundColor: "#cccccc",
//                   color: "#666666"
//                 }
//               }}
//             >
//               {loading ? (
//                 <CircularProgress size={24} sx={{ color: "white" }} />
//               ) : (
//                 editingRecord ? "Update Payroll" : "Save Payroll"
//               )}
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </Box>
//     </LocalizationProvider>
//   );
// }